import { InjectionToken, Type, inject } from '@angular/core';
import { CanMatchFn, Route, Router, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { LazyFeatureLoadError } from './stale-chunk-recovery.service';

/**
 * Whether there is a signed-in session — the cheap, local part of AuthGuard (no API call). Used to
 * avoid fetching feature chunks for signed-out users; permissions are still checked by AuthGuard on
 * every child route. A token so router tests can stub it without AuthService's dependencies.
 */
export const HAS_SESSION = new InjectionToken<() => boolean>('HAS_SESSION', {
  providedIn: 'root',
  factory: () => {
    const auth = inject(AuthService);
    return () => {
      const { currentUser, loggedIn } = auth.getSocialUser();
      return !!currentUser && !!loggedIn;
    };
  },
});

/** Signed-out users go to /login before the feature chunk is requested (same target as AuthGuard). */
const requireSession: CanMatchFn = () =>
  inject(HAS_SESSION)() || inject(Router).createUrlTree(['/login']);

/** A lazy feature route remembers its paths, since the router config only holds its matcher. */
type LazyFeatureRoute = Route & { lazyFeaturePaths: string[] };

/**
 * Builds a lazy route for an entity feature whose child routes keep their full paths
 * (`items`, `items/:id`, `releases/series/:id`...).
 *
 * The matcher consumes no segments, it only decides whether the URL belongs to the feature, so the
 * child routes resolve exactly as they did when they were top-level: same `routeConfig.path`, same
 * `snapshot.url`, same params. Components did not need to change to become lazy.
 *
 * `paths` must list every path the feature's .route.ts defines — lazy-features.spec.ts checks this.
 *
 * Load failures are rethrown as LazyFeatureLoadError so StaleChunkRecoveryService can reload a tab
 * that is still running a previous deploy. Signed-out users are redirected by `canMatch` before the
 * chunk is downloaded.
 */
export function lazyFeatureRoute(paths: string[], loadChildren: () => Promise<Type<unknown>>): Route {
  const patterns = paths.map((p) => p.split('/'));
  const route: LazyFeatureRoute = {
    matcher: (segments: UrlSegment[]): UrlMatchResult | null =>
      patterns.some((pattern) => matchesPattern(pattern, segments)) ? { consumed: [] } : null,
    canMatch: [requireSession],
    loadChildren: () =>
      loadChildren().catch((error) => {
        throw new LazyFeatureLoadError(error);
      }),
    // Not in `data` on purpose: data on this componentless parent would be inherited by every child.
    lazyFeaturePaths: paths,
  };
  return route;
}

/**
 * Every path in a router config, including the ones behind lazy feature routes — those only expose
 * a matcher, so reading `route.path` from `router.config` misses them. Use this instead of
 * iterating `router.config` directly when you need to know which pages exist.
 */
export function configuredPaths(routes: Routes): string[] {
  return routes.flatMap(
    (route) => (route as LazyFeatureRoute).lazyFeaturePaths ?? (route.path !== undefined ? [route.path] : []),
  );
}

function matchesPattern(pattern: string[], segments: UrlSegment[]): boolean {
  return (
    pattern.length === segments.length &&
    pattern.every((part, i) => part.startsWith(':') || part === segments[i].path)
  );
}

/** Child routes for a lazy feature module, as its <name>.route.ts defines them. */
export function toChildRoutes(feature: { routes: () => Route[] }): Route[] {
  return feature.routes();
}
